import { ReactNode } from 'react';
import { ColumnGroup } from '../types';

export interface PresentationCtx {
  terms: ClaimTerms;
  yearSnapshots: YearSnapshot[];
}

export interface StrategyPresentation {
  // Extra column groups appended to the universal payout table after the
  // standard When / Cost / Benefit / Payout / Pool / DB groups.
  extraPayoutColumnGroups?: (ctx: PresentationCtx) => ColumnGroup[];

  // Sub-rows rendered inside the universal PaidCell, below the headline
  // payout amount. Returns null when this strategy has no extras for this
  // month. Strategy owns its own casts into `month.deltas.strategyDeltas`
  // and `month.state.strategyState`.
  paidCellExtras?: (month: MonthlyEntry) => ReactNode;

  // Per-cell tooltip content for the universal LTC Pool cell. When provided,
  // the cell wraps in a <Tooltip>. Return null to suppress the hover on
  // rows where there's nothing meaningful to show. Distinct from
  // `copy.poolTooltip` which is the column-header description.
  poolCellTooltip?: (ctx: {
    month: MonthlyEntry;
    openState: MonthlyState;
    terms: ClaimTerms;
  }) => ReactNode | null;

  // Per-cell tooltip content for the universal Death Benefit cell.
  deathBenefitCellTooltip?: (ctx: {
    month: MonthlyEntry;
    openState: MonthlyState;
    terms: ClaimTerms;
  }) => ReactNode | null;

  // Per-cell tooltip for the PaidCell, explaining why this row's payout
  // is what it is (cost vs cap, pool exhaustion, reimbursement rule, etc.).
  // Returns null when there's nothing to explain (e.g., a $0 row in a phase
  // that hasn't started paying).
  payoutCellTooltip?: (ctx: { month: MonthlyEntry; terms: ClaimTerms }) => ReactNode | null;

  // Extra content appended to the BenefitAmountCell's inflation tooltip.
  // Lets a strategy explain how this year's monthly cap relates to other
  // policy facts (for Asset Flex: the at-issue Acceleration pool derivation).
  benefitAmountCellTooltipFooter?: (ctx: {
    snapshot: YearSnapshot;
    terms: ClaimTerms;
  }) => ReactNode | null;

  // Override for the rate displayed in the BenefitAmountCell's inflation
  // tooltip. Defaults to `terms.inflationRate`. Asset Flex uses this to
  // surface the On-Face ACIO override (`baseBenefitPoolInflationRate`)
  // when it differs from the policy-wide ACIO, so the tooltip's
  // base × (1+r)^n math agrees with the actual monthlyBenefitCap.
  benefitInflationRate?: (terms: ClaimTerms) => number;

  // Copy overrides on universal cells. Universal defaults win when absent.
  copy?: {
    poolTooltip?: string;
    payoutColumnTooltip?: string;
    benefitInflationCompoundLabel?: string;
    benefitInflationCapNote?: (capYear: number) => string;
  };
}

export const defaultPresentation: StrategyPresentation = {};
